import React from 'react';
import { Modal, Radio, TimePicker, Button, Input, Form, Typography } from 'antd';
import type { RadioChangeEvent } from 'antd';
import dayjs from 'dayjs';

const { Text } = Typography;
const { TextArea } = Input;

type Props = {
    open: boolean;
    onClose: () => void;
    onSubmit: (data: {
        mode: 'adjust' | 'exempt';
        inTime?: string;
        outTime?: string;
        note: string;
        date: string;
    }) => void;
    date: string;
};

const AttendanceRegularizationModal: React.FC<Props> = ({ open, onClose, onSubmit, date }) => {
    const [form] = Form.useForm();
    const mode = Form.useWatch('mode', form) || 'adjust';

    const handleFinish = (values: any) => {
        const { mode, inTime, outTime, note } = values;
        onSubmit({
            mode,
            inTime: mode === 'adjust' ? dayjs(inTime).format('HH:mm') : undefined,
            outTime: mode === 'adjust' ? dayjs(outTime).format('HH:mm') : undefined,
            note,
            date
        });
    };

    return (
        <Modal
            title={<span className="text-lg font-semibold">Request Attendance Regularization - {date}</span>}
            open={open}
            onCancel={onClose}
            footer={<div className="flex justify-end gap-2">
                <Button onClick={onClose}>
                    Cancel
                </Button>
                <Button type="primary" onClick={() => form.submit()}>
                    Request
                </Button>
            </div>}
            centered
            className="!top-10"
            bodyStyle={{ backgroundColor: '#fff' }}
            style={{ maxWidth: 600 }}
            maskClosable={false}
        >
            <Form
                form={form}
                layout="vertical"
                onFinish={handleFinish}
                initialValues={{
                    mode: 'adjust',
                    inTime: dayjs('10:00', 'HH:mm'),
                    outTime: dayjs('19:00', 'HH:mm'),
                    note: '',
                }}
                className="p-4"
            >
                <Form.Item name="mode">
                    <Radio.Group className="flex flex-col gap-2">
                        <Radio value="adjust">
                            Add/update time entries to adjust attendance logs.
                        </Radio>
                        <Radio value="exempt">
                            Raise regularization request to exempt this day from tracking policy penalization.
                        </Radio>
                    </Radio.Group>
                </Form.Item>

                {mode === 'adjust' && (
                    <div className="flex flex-col gap-4 mb-4">
                        <Text className="font-medium text-gray-700">Attendance Adjustment</Text>
                        <div className="flex gap-6">
                            <Form.Item
                                name="inTime"
                                label={<span className="text-xs text-green-500">Clock In</span>}
                                rules={[{ required: true, message: 'Please select clock-in time' }]}
                            >
                                <TimePicker format="hh:mm A" use12Hours className="w-full" />
                            </Form.Item>
                            <Form.Item
                                name="outTime"
                                label={<span className="text-xs text-red-500">Clock Out</span>}
                                rules={[{ required: true, message: 'Please select clock-out time' }]}
                            >
                                <TimePicker format="hh:mm A" use12Hours className="w-full" />
                            </Form.Item>
                        </div>
                    </div>
                )}

                <Form.Item
                    name="note"
                    label={<Text className="font-medium text-gray-700">Note</Text>}
                    rules={[{ required: true, message: 'Please provide a note' }]}
                >
                    <TextArea rows={3} />
                </Form.Item>


            </Form>
        </Modal>
    );
};

export default AttendanceRegularizationModal;
